import {
  Row,
  Col,
  Form,
  Card,
  Button,
  Tooltip,
  InputGroup,
  SplitButton,
  OverlayTrigger
} from "react-bootstrap";
import moment from "moment";
import styled from "styled-components";
import _debounce from "lodash/debounce";
import { components } from "react-select";
import { useSelector } from "react-redux";
import {
  faPen,
  faUser,
  faPhone,
  faXmark,
  faClock,
  faRobot,
  faBellSlash,
  faPhoneSlash,
  faRotateLeft,
  faCircleCheck,
  faXmarkCircle,
  faLocationDot,
  faCalendarDays,
  faPersonCircleExclamation
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import DropdownItem from "react-bootstrap/esm/DropdownItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  ICON_TYPE,
  COUNTRY_GERMANY,
  ACCESS_CONFIG,
  ACTIVITY_TYPES,
  SEARCH_LEAD_BY,
  APPOINTMENT_TYPE,
  DATE_TIME_FORMAT,
  FUNNEL_TYPE_FILTER,
  APPOINTMENT_STATUS,
  ACTIVITY_RESULT_TYPES,
  SCRIPT_WORKSPACE_PATH,
  SCRIPT_LOCAL_FILE_NAME,
  LEAD_INTERACTION_STATUS,
  DATE_TIME_WITH_MONTH_FORMAT,
  LEAD_AGENT_TIMESTAMP_UPDATE_DELAY,
  AUTOPILOT_VIEW_STATE,
  autopilotViewState
} from "../strings";
import {
  CustomCard,
  HorizontalDivider,
  AgentViewActivityBtn
} from "../common-styling";
import {
  CustomDiv,
  CustomBadge,
  DotSeparator,
  AddressColumn,
  SelectContainer,
  CustomCardHeader,
  CalcDistanceText,
  CustomFontSizeDiv,
  AnimationContainer,
  ExtraInfoCustomBadge,
  AbsoluteInfoContainer,
  ControlledHeightContainer,
  ControlledHeightLoaderContainer
} from "./lead-agent-styles";
import Loader from "../loader/loader";
import {
  countryCodesHash,
  leadSearchOptions
} from "../common-confgs/common-vars";
import {
  generateDateTime,
  generateScriptUrl,
  leadAgentSessionExpiry
} from "../../services/common-service";
import {
  formatBusiness,
  localToInstant,
  instantToLocalInput
} from "../../services/business-time";
import {
  createActivity,
  updateLeadById,
  getLeadsBySearch,
  getLeadActivities,
  assignLeadToAgent,
  unassignLeadFromAgent,
  takeoverAiLeadserviceLead,
  getFunnelsForLeadsBySearch,
  validateAndUpdateLeadAgentInfo
} from "../../services/api-declaration";
import {
  LeadUpdateSchema,
  LeadAppointmentUpdateSchema
} from "../../services/validation-schema";
import PickerDialog from "./picker-dialog";
import ActivityButton from "./activity-button";
import { useDialog } from "../../hooks/useDialog";
import { hasAccess } from "../../services/access";
import AgentPricePrompt from "./agent-price-prompt";
import CountryFlagEmoji from "../country-flag-emoji";
import LeadActivities from "../leads/lead-activities";
import PDFViewer from "../leadservice-panel/pdf-viewer";
import PreventReload from "../funnel-form/prevent-reload";
import AppointmentPickerDialog from "./datetime-picker-dialog";
import EditCollapsableActions from "./edit-collapsable-actions";
import InterviewSummary from "./interview-summary";
import AutopilotCard from "./autopilot-card";
import AvailabilityBadgeContent from "./availability-badge-content";
import Option from "../reusable-form-fields/select-custom-option-item";
import { useAgentAssignment } from "../../context/agent-assignment-context";
import useBlockInCallNavigation from "../../hooks/useBlockInCallNavigation";
import { showError, showInfo, showSuccess } from "../../services/toast-service";
import { useTranslation, Trans } from "react-i18next";

const MapLink = styled.a`
  padding: 2px 6px;
  border-radius: 6px;
  border: 1px solid var(--color-light-gray);
`;
const NotesTextArea = styled(Form.Control)`
  font-size: 16px;
  border: 1px solid var(--field-border-color-regular);
  &:focus-visible {
    outline: none;
  }
`;

const getClientTimezoneInfo = () => ({
  clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  clientUtcOffsetMinutes: new Date().getTimezoneOffset()
});

// Appointment and callback times are German time, whatever timezone the PC
// runs on (services/business-time.js).
const generateBusinessDateTime = (date, format = DATE_TIME_FORMAT) =>
  !date ? "---" : formatBusiness(date, format);

const {
  CALL,
  NAME_WRONG,
  NOT_REACHED,
  NOT_SUITABLE,
  NUMBER_WRONG,
  QUALIFIED,
  CONTACT_LATER,
  APPOINTMENT_CALL,
  APPOINTMENT_CANCELLED,
  APPOINTMENT_CONFIRMED,
  APPOINTMENT_POSTPONED,
  APPOINTMENT_NOT_REACHED
} = ACTIVITY_RESULT_TYPES;

const defaultCurrentActivityLoader = {
  [CALL]: false,
  [NUMBER_WRONG]: false,
  [NOT_REACHED]: false,
  [NAME_WRONG]: false,
  [QUALIFIED]: false,
  [NOT_SUITABLE]: false,
  [CONTACT_LATER]: false,
  [APPOINTMENT_CALL]: false,
  [APPOINTMENT_CANCELLED]: false,
  [APPOINTMENT_CONFIRMED]: false,
  [APPOINTMENT_POSTPONED]: false,
  [APPOINTMENT_NOT_REACHED]: false
};

const MenuList = (props) => {
  const { MenuListHeader = null, MenuListFooter = null } =
    props.selectProps.components;
  return (
    <components.MenuList {...props}>
      {props.children.length && MenuListHeader}
      {props.children}
      {props.children.length && MenuListFooter}
    </components.MenuList>
  );
};
const MenuListFooter = ({ showing, total, onClick, isDisabled }) => {
  const { t } = useTranslation();
  return !showing || showing >= total ? null : (
    <Button
      className="mt-1 ms-2"
      variant="secondary"
      onClick={onClick}
      disabled={isDisabled}
    >
      {t("leadAgent.loadMore")}
    </Button>
  );
};

const defaultEditMode = {
  leadPhone: false,
  leadEmail: false,
  leadName: false,
  appointmentOn: false
};

const LeadAgentPanelView = () => {
  const { t } = useTranslation();
  const { registerAgentAssignment } = useAgentAssignment();
  const user = useSelector((state) => state.auth.user);
  const globalReclaimConfig = useSelector(
    (state) => state.auth.user.workspace.globalReclaimConfig
  );
  const navigate = useNavigate();
  const timer = useRef();
  const phoneNumberClickRef = useRef();
  const pageIndex = useRef(1);
  const searchLoader = useRef(false);
  const [lead, setLead] = useState({});
  const [comment, setComment] = useState("");
  const [systemGeneratedBudget, setSystemGeneratedBudget] = useState("");
  const [isQualified, setIsQualified] = useState(false);
  const [availableAfterFlag, setAvailableAfterFlag] = useState(false);
  const [contactLaterFlag, setContactLaterFlag] = useState(false);
  const [searchType, setSearchType] = useState(SEARCH_LEAD_BY.PHONE);
  const [leadSearchVia, setLeadSearchVia] = useState(
    lead?.appointmentOn ? SEARCH_LEAD_BY.APPOINTMENT : SEARCH_LEAD_BY.AUTO
  );
  const [activities, setActivities] = useState([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [emailsToSend, setEmailsToSend] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activityLoader, setActivityLoader] = useState(false);
  const [takeoverLoader, setTakeoverLoader] = useState(false);
  const [currentActivityLoader, setCurrentActivityLoader] = useState(
    defaultCurrentActivityLoader
  );
  const [updateLoader, setUpdateLoader] = useState(false);
  const [paginateOptions, setPaginateOptions] = useState({
    totalDocs: 0
  });
  const [options, setOptions] = useState([]);
  const [editMode, setEditMode] = useState(defaultEditMode);
  const [funnelAppointment, setFunnelAppointment] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [interactionStatus, setInteractionStatus] = useState("");
  const isAgentInCall = interactionStatus === LEAD_INTERACTION_STATUS.INITIATED;
  const [selectedFunnel, setSelectedFunnel] = useState(null);
  const [showPickerDialog, setShowPickerDialog] = useState(false);
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [responses, setResponses] = useState({});
  const [
    isLeadServicePricePromptRequired,
    setIsLeadServicePricePromptRequired
  ] = useState(false);
  const [
    isLeadServicePricePromptAnswered,
    setIsLeadServicePricePromptAnswered
  ] = useState(true);
  // A&M Autopilot (Asana 1219547818089406): answers typed in the Autopilot
  // card go to the Autopilot with "später" / "nicht geeignet" too
  const [autopilotAnswers, setAutopilotAnswers] = useState([]);
  const [autopilotBudget, setAutopilotBudget] = useState("");
  const resetAutopilotInput = useCallback(() => {
    setAutopilotAnswers([]);
    setAutopilotBudget("");
  }, []);
  const { showAlertDialogBox } = useDialog();
  const isAiLeadserviceClosedLead = lead?.aiLeadserviceSent;
  // a booking through the Autopilot card runs; no second result meanwhile
  const [isAutopilotBooking, setIsAutopilotBooking] = useState(false);
  const areLeadActionsDisabled = activityLoader || isAutopilotBooking;
  const mandatoryLeadServiceQuestions =
    lead?.funnelId?.leadServiceQuestions?.filter((question) =>
      question.name?.trim()
    ) || [];
  const areMandatoryLeadServiceQuestionsAnswered =
    mandatoryLeadServiceQuestions.every((question) =>
      responses[question.name]?.trim()
    );
  const isLeadServicePricePromptComplete =
    !isLeadServicePricePromptRequired || isLeadServicePricePromptAnswered;
  const hasMissingMandatoryLeadServiceAnswers =
    !areMandatoryLeadServiceQuestionsAnswered ||
    !isLeadServicePricePromptComplete;
  const canQualifyLead =
    Boolean(comment?.trim()) &&
    areMandatoryLeadServiceQuestionsAnswered &&
    isLeadServicePricePromptComplete;
  // the Lead Service finishes the Autopilot's job: questions and booking
  // through the Autopilot card, no "Qualifiziert" without an appointment
  const leadAutopilotState = autopilotViewState(lead);
  const isAutopilotLead = [
    AUTOPILOT_VIEW_STATE.WITH_AUTOPILOT,
    AUTOPILOT_VIEW_STATE.IN_LEADSERVICE
  ].includes(leadAutopilotState);
  // switched off (AC 36): the normal Lead Service flow, the chat read-only
  const isAutopilotEndedLead =
    leadAutopilotState === AUTOPILOT_VIEW_STATE.ENDED;
  // an appointment of an earlier cycle must not hide the Autopilot card
  const isUnbookedAndNotSentToAiLeadService =
    (isAutopilotLead || !lead?.appointmentOn) && !isAiLeadserviceClosedLead;
  const showCustomerAppointmentLink =
    lead.funnelId?.customerAppointmentLink && !isAutopilotLead;
  const showLeadServiceQuestions =
    lead.funnelId?.leadServiceQuestions?.length > 0 && !isAutopilotLead;

  const usualInitialVals = {
    leadEmail: "",
    leadPhone: "",
    leadName: "",
    isInCall: false
  };
  const appointmentInitialVals = {
    isInCall: false,
    appointmentOn: ""
  };
  const methods = useForm({
    resolver: yupResolver(
      lead?.appointmentOn ? LeadAppointmentUpdateSchema : LeadUpdateSchema
    ),
    defaultValues: lead?.appointmentOn
      ? appointmentInitialVals
      : usualInitialVals
  });
  const {
    reset,
    watch,
    register,
    setValue,
    getValues,
    formState: { errors }
  } = methods;

  const togglePickerDialog = () => setShowPickerDialog(!showPickerDialog);
  const toggleAppointmentDialog = () =>
    setShowAppointmentDialog(!showAppointmentDialog);
  const toggleDialog = ({
    leadServicePlus,
    leadServiceIndividualNote,
    reClaimNote
  }) => {
    let dialogType = "warning";
    let note = leadServiceIndividualNote;
    let message = t("leadAgent.individualNoteWarning");

    if (reClaimNote) {
      note = reClaimNote;
      dialogType = "primary";
    } else {
      if (leadServiceIndividualNote) {
        dialogType = "danger";
        if (leadServicePlus) {
          message = (
            <div
              dangerouslySetInnerHTML={{
                __html: `<div>${t(
                  "leadAgent.leadServicePlusWarning"
                )}</div><div>${t("leadAgent.individualNoteWarning")}</div>`
              }}
            ></div>
          );
        }
      } else if (leadServicePlus) {
        message = t("leadAgent.leadServicePlusWarning");
      }
    }

    showAlertDialogBox({
      title: t("leadAgent.warningPopupTitle"),
      message,
      acceptLabel: t("common.understood"),
      dialogType,
      lsIndividualNote: note
    });
  };

  const leadServiceIndividualNote =
    (lead?.reclaimed && globalReclaimConfig.leadServiceIndividualNote) ||
    lead?.leadServiceIndividualNote;

  const leadData = getValues();
  const shouldContactLater = (_contactLater) =>
    _contactLater && moment().isBefore(moment(_contactLater));
  const checkLeadAvailability = (_availableAfter) =>
    moment(_availableAfter) > moment(Date.now());
  const generatePhoneNumber = (leadPhoneNumber) => {
    let phoneNumber = leadPhoneNumber;
    const country = lead.funnelId?.companyInfo?.country;
    const countryCode = countryCodesHash[country];
    if (leadPhoneNumber.charAt(0) === "0" && countryCode) {
      phoneNumber = `${countryCode}${leadPhoneNumber.substring(1)}`;
    } else {
      phoneNumber = leadPhoneNumber;
    }
    return phoneNumber;
  };
  const setCommentPrice = (priceRange) => {
    if (priceRange) {
      const { lowerBound, upperBound } = priceRange;
      // check deviation = 0%
      if (lowerBound === upperBound) {
        setSystemGeneratedBudget(`Budgetrahmen: Ab ${lowerBound}€`);
      } else {
        setSystemGeneratedBudget(
          `Budgetrahmen: ${lowerBound}€ - ${upperBound}€\n`
        );
      }
    }
  };
  const handleLeadServicePricePromptCompletion = ({
    isRequired,
    isAnswered
  }) => {
    setIsLeadServicePricePromptRequired(isRequired);
    setIsLeadServicePricePromptAnswered(isAnswered);
  };

  const fetchLeadActivities = useCallback(async (leadId) => {
    try {
      setIsLoadingActivities(true);
      const response = await getLeadActivities(leadId);
      setActivities(response.data.data);
    } catch (error) {
      console.log("Error in getting lead activites ", error);
    } finally {
      setIsLoadingActivities(false);
    }
  }, []);
  const fetchAndAssignLeadToAgent = useCallback(
    async ({ leadId = null, funnelId = null, searchType }) => {
      setFunnelAppointment(false); // need to test
      try {
        setIsLoading(true);
        resetSearchFieldAndOpts();
        setEditMode(defaultEditMode);
        const { data } = await assignLeadToAgent({ leadId, funnelId });
        const lead = data.data.lead;
        setEmailsToSend(data.data.emailsToSend);
        setLead(lead || {});
        setComment("");
        setSystemGeneratedBudget("");
        setResponses({});
        setIsLeadServicePricePromptRequired(false);
        setIsLeadServicePricePromptAnswered(true);
        if (lead) {
          if (lead.funnelId?.customerAppointmentLink) {
            setFunnelAppointment(true);
          }
          const {
            leadServicePlus,
            leadServiceIndividualNote,
            assignedVia,
            appointmentOn
          } = lead;
          const reClaimNote =
            lead.reclaimed && globalReclaimConfig.leadServiceIndividualNote;
          if (
            (leadServicePlus || leadServiceIndividualNote || reClaimNote) &&
            !appointmentOn
          ) {
            toggleDialog({
              leadServicePlus,
              leadServiceIndividualNote,
              reClaimNote
            });
          }
          const _searchVia = assignedVia ? assignedVia : searchType;
          setLeadSearchVia(_searchVia);
          if (_searchVia === SEARCH_LEAD_BY.FUNNEL) {
            setSelectedFunnel(lead.funnelId);
          }
          const { leadName, leadEmail, leadPhone } = lead.leadDetails;
          reset({
            leadName,
            leadEmail,
            leadPhone,
            appointmentOn: appointmentOn
              ? instantToLocalInput(appointmentOn)
              : ""
          });
          const isInitiated =
            lead.interactionStatus === LEAD_INTERACTION_STATUS.INITIATED;
          setValue("isInCall", isInitiated, { shouldDirty: isInitiated });
          setInteractionStatus(lead.interactionStatus);
          setIsQualified(lead.leadQualified);
          if (checkLeadAvailability(lead.availableAfter)) {
            setAvailableAfterFlag(true);
          } else {
            setAvailableAfterFlag(false);
          }
          if (shouldContactLater(lead.contactLater)) {
            setContactLaterFlag(true);
          } else {
            setContactLaterFlag(false);
          }
          await fetchLeadActivities(lead._id);
        }
      } catch (error) {
        console.log("Error in assign lead: ", error);
        showError(error.response?.data?.message || "Error in assign lead");
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line
    [reset, setValue, fetchLeadActivities]
  );

  const fetchFunnelOptions = useMemo(
    () =>
      _debounce(async (inputValue) => {
        try {
          if (inputValue) {
            const { data } = await getFunnelsForLeadsBySearch({
              funnelName: inputValue,
              pageIndex: pageIndex.current
            });
            const { totalDocs, rows } = data.data;
            setPaginateOptions({
              totalDocs: rows.length ? totalDocs : 0
            });
            rows.map((row) => {
              if (row.leadsCount === 0) {
                row.disabled = true;
                row.iconType = ICON_TYPE.NO_LEADS;
              }
              if (interactionStatus === LEAD_INTERACTION_STATUS.INITIATED) {
                row.disabled = true;
              }
              return row;
            });
            setOptions((prev) => [...prev, ...rows]);
          }
        } catch (err) {
          console.log(err);
          showError(
            err?.response?.data?.message ||
              t("toastMessage.errorInGetting", { what: "funnels" })
          );
        } finally {
          searchLoader.current = false;
        }
      }, 500),
    [t, interactionStatus]
  );
  const fetchLeadOptions = useMemo(
    () =>
      _debounce(async (inputValue) => {
        try {
          if (inputValue) {
            const { data } = await getLeadsBySearch({
              phone: searchType === SEARCH_LEAD_BY.PHONE ? inputValue : null,
              email: searchType === SEARCH_LEAD_BY.EMAIL ? inputValue : null,
              pageIndex: pageIndex.current
            });
            const { totalDocs, rows } = data.data;
            const docs = rows.length ? totalDocs : 0;
            setPaginateOptions({ totalDocs: docs });
            rows.map((row) => {
              if (interactionStatus === LEAD_INTERACTION_STATUS.INITIATED) {
                row.disabled = true;
              }

              if (row.aiLeadserviceSent) {
                row.iconType = ICON_TYPE.AI_LEADSERVICE_SENT;
              } else if (
                row.interactionStatus === LEAD_INTERACTION_STATUS.INITIATED
              ) {
                if (
                  row.leadAgentTimestamp &&
                  new Date(row.leadAgentTimestamp) < leadAgentSessionExpiry()
                ) {
                  return row;
                }
                row.disabled = true;
                row.iconType = ICON_TYPE.ALREADY_INITIATED;
              } else if (
                row.postponedTill &&
                checkLeadAvailability(row.postponedTill)
              ) {
                row.iconType = ICON_TYPE.NOT_AVAILABLE_2H;
              } else if (
                row.appointmentStatus === APPOINTMENT_STATUS.CONFIRMED
              ) {
                row.iconType = ICON_TYPE.APPOINTMENT_CONFIRMED;
                row.disabled = true;
              } else if (
                row.appointmentStatus === APPOINTMENT_STATUS.CANCELLED
              ) {
                row.iconType = ICON_TYPE.APPOINTMENT_CANCELLED;
                row.disabled = true;
              } else if (!row.appointmentConfirmation && row.appointmentOn) {
                row.iconType = ICON_TYPE.NO_CONFIRMATION;
              } else if (
                !row.appointmentOn &&
                row.appointmentLink &&
                row.leadQualified
              ) {
                row.iconType = ICON_TYPE.APPOINTMENT_NOT_TAKEN;
              } else if (row.appointmentOn) {
                row.iconType = ICON_TYPE.APPOINTMENT_TAKEN;
              } else if (row.leadQualified) {
                row.iconType = ICON_TYPE.ALREADY_QUALIFIED;
              } else if (
                row.availableAfter &&
                checkLeadAvailability(row.availableAfter)
              ) {
                row.iconType = ICON_TYPE.NOT_AVAILABLE_NOW;
              } else if (
                row.serviceAgent &&
                user._id !== row.serviceAgent._id
              ) {
                row.disabled = true;
                row.iconType = ICON_TYPE.ALREADY_ASSIGNED;
              } else if (shouldContactLater(row.contactLater)) {
                row.iconType = ICON_TYPE.CONTACT_LATER;
              } else {
                row.iconType = null;
              }
              return row;
            });
            setOptions((prev) => [...prev, ...rows]);
          }
        } catch (err) {
          console.log(err);
          showError(
            err?.response?.data?.message ||
              t("toastMessage.errorInGetting", { what: "leads" })
          );
        } finally {
          searchLoader.current = false;
        }
      }, 500),
    [t, user, searchType, interactionStatus]
  );
  const activityHandler = async ({
    type,
    result,
    comment,
    responses,
    contactLaterVal = "",
    contactLaterLocal,
    appointmentDetails = null
  }) => {
    try {
      const isCallAttempt = [CALL, APPOINTMENT_CALL].includes(result);
      if (isCallAttempt && isAiLeadserviceClosedLead) {
        showInfo(t("leadAgent.aiLeadserviceClosedToast"));
        return;
      }
      if (areLeadActionsDisabled) return;
      if (result === QUALIFIED && !canQualifyLead) {
        showError(t("leadAgent.mandatoryLeadServiceAnswersRequired"));
        return;
      }
      setActivityLoader(true);
      setCurrentActivityLoader({
        ...defaultCurrentActivityLoader,
        [result]: true
      });
      // If lead is coming by search, then check for service agent before call
      if (isCallAttempt) {
        if (lead.serviceAgent !== user._id) {
          await validateAndUpdateLeadAgentInfo(lead._id, {
            shouldAssignAgent: true,
            searchVia: leadSearchVia
          });
        }
        // click on hidden call anchor element
        phoneNumberClickRef.current?.click();
      }
      const { data } = await createActivity(
        {
          type,
          result,
          comment,
          searchVia: leadSearchVia,
          ...getClientTimezoneInfo(),
          contactLaterVal,
          contactLaterLocal,
          appointmentDetails,
          responses,
          ...(isAutopilotLead && {
            autopilotAnswers,
            budgetAmount: autopilotBudget || undefined
          })
        },
        lead._id
      );
      showSuccess(data?.message);
      setComment("");
      setSystemGeneratedBudget("");
      setResponses({});
      setIsLeadServicePricePromptRequired(false);
      setIsLeadServicePricePromptAnswered(true);
      resetAutopilotInput();

      if (
        data.data.contactAttempts === 10 &&
        result !== CALL &&
        result !== APPOINTMENT_CALL
      ) {
        fetchAndAssignLeadToAgent({});
        return;
      }
      if (result === CALL || result === APPOINTMENT_CALL) {
        setInteractionStatus(LEAD_INTERACTION_STATUS.INITIATED);
        resetSearchFieldAndOpts();
        setValue("isInCall", true, { shouldDirty: true });
      } else if (
        [
          NAME_WRONG,
          NUMBER_WRONG,
          NOT_REACHED,
          QUALIFIED,
          NOT_SUITABLE,
          CONTACT_LATER,
          APPOINTMENT_POSTPONED,
          APPOINTMENT_CONFIRMED,
          APPOINTMENT_CANCELLED,
          APPOINTMENT_NOT_REACHED
        ].includes(result)
      ) {
        setInteractionStatus(LEAD_INTERACTION_STATUS.HANDLED);
        fetchAndAssignLeadToAgent({
          funnelId: selectedFunnel?._id,
          searchType
        });
      } else {
        setInteractionStatus(LEAD_INTERACTION_STATUS.HANDLED);
      }
    } catch (error) {
      console.log(error);
      if (error.response?.data?.data?.isAlreadyTaken) {
        showError(t("leadAgent.alreadyTakenMsg"));
        navigate(0);
      } else if (error.response?.data?.data?.code === "panel_action_running") {
        showError(t("leadAgent.autopilot.actionRunning"));
      } else {
        showError(error.response?.data?.message);
      }
    } finally {
      setCurrentActivityLoader(defaultCurrentActivityLoader);
      setActivityLoader(false);
    }
  };
  // booking through the Autopilot card is the result of the call
  const handleAutopilotBooked = () => {
    setComment("");
    resetAutopilotInput();
    setInteractionStatus(LEAD_INTERACTION_STATUS.HANDLED);
    fetchAndAssignLeadToAgent({
      funnelId: selectedFunnel?._id,
      searchType
    });
  };
  const handleUpdateLead = async (formValues) => {
    try {
      if (lead._id) {
        if (updateLoader) return;
        setUpdateLoader(true);
        // The form holds the appointment as wall clock in German time. An
        // edited time is sent as typed; otherwise the stored instant goes
        // back unchanged (a lead with an appointment is saved through the
        // appointment branch of the API, as before).
        const { appointmentOn: appointmentOnLocal, ...leadValues } = formValues;
        let appointmentUpdate = {};
        if (editMode.appointmentOn) {
          appointmentUpdate = {
            appointmentOnLocal,
            appointmentOn: localToInstant(appointmentOnLocal).toISOString(),
            ...getClientTimezoneInfo()
          };
        } else if (lead.appointmentOn) {
          appointmentUpdate = { appointmentOn: lead.appointmentOn };
        }
        const response = await updateLeadById(lead._id, {
          ...leadValues,
          ...appointmentUpdate
        });
        setLead({
          ...lead,
          notCallable: response.data?.data?.notCallable,
          ...(editMode.appointmentOn && {
            appointmentOn: appointmentUpdate.appointmentOn
          })
        });
        showSuccess(response.data.message);
        setEditMode(defaultEditMode);
      }
    } catch (error) {
      showError(
        error?.response?.data?.message || t("toastMessage.updateLeadError")
      );
      console.log(error);
    } finally {
      setUpdateLoader(false);
    }
  };
  const handleZipCodeValue = (_zipCode) => {
    let zipCode = _zipCode;
    if (
      zipCode?.length === 4 &&
      lead.funnelId?.companyInfo?.country === COUNTRY_GERMANY
    ) {
      // concating zero to zipCode if length is 4 and country is germany!
      zipCode = `0${zipCode}`;
    }
    return zipCode;
  };
  const generateGoogleMapLink = (_lead) => {
    const customerZipCode =
      lead.funnelId?.alternativeAddress?.enabled &&
      lead.funnelId?.alternativeAddress?.zipCode
        ? lead.funnelId.alternativeAddress.zipCode
        : _lead.funnelId?.customer?.zipCode;
    const leadZipCode = handleZipCodeValue(
      _lead.leadAddress?.zipCode || _lead.leadDetails?.leadZip
    );
    return `https://www.google.com/maps/dir/+${customerZipCode},/+${leadZipCode}`;
  };
  const resetSearchFieldAndOpts = () => {
    setOptions([]);
    setInputValue("");
  };
  const onSelectSearchType = (type) => {
    if (isAgentInCall || searchType === type) return;
    resetSearchFieldAndOpts();
    setSearchType(type);
  };
  const generateOptLabelString = (opt) => {
    if (!opt) return;
    return `${
      searchType === SEARCH_LEAD_BY.PHONE
        ? `${opt.leadDetails?.leadName} (${opt.leadDetails?.leadPhone})`
        : searchType === SEARCH_LEAD_BY.EMAIL
          ? `${opt.leadDetails?.leadEmail} (${opt.leadDetails?.leadPhone})`
          : searchType === SEARCH_LEAD_BY.FUNNEL
            ? `${opt.general?.funnelName} (${opt.general?.funnelType})`
            : `---`
    }`;
  };
  const onSearchInputChange = (searchVal, isnum) => {
    if (
      searchType === SEARCH_LEAD_BY.FUNNEL ||
      searchType === SEARCH_LEAD_BY.EMAIL ||
      (searchType === SEARCH_LEAD_BY.PHONE && isnum)
    ) {
      pageIndex.current = 1;
      searchLoader.current = searchVal ? true : false;
      setOptions([]);
      setInputValue(searchVal);
    }
  };
  const handleInputChange = (action, _inputValue) => {
    if (isAgentInCall || action !== "input-change") return;
    const isnum = /^\d+$/.test(_inputValue);
    onSearchInputChange(_inputValue, isnum);
    if (searchType === SEARCH_LEAD_BY.FUNNEL) {
      fetchFunnelOptions(_inputValue);
    } else if (
      searchType === SEARCH_LEAD_BY.EMAIL ||
      (searchType === SEARCH_LEAD_BY.PHONE && isnum)
    ) {
      fetchLeadOptions(_inputValue);
    } else {
      resetSearchFieldAndOpts();
    }
  };
  const isEditing = (_editMode) => _editMode === true;
  const isEditModeEnabled = Object.values(editMode).some(isEditing);
  const handleEditMode = (property) =>
    setEditMode({
      ...editMode,
      [property]: true
    });
  const cancelEdit = () => {
    setEditMode(defaultEditMode);
    reset({ ...getValues() }, { keepDirty: watch("isInCall") });
  };
  const checkIfZipCodeIsMissing = (_lead) => {
    const alternativeZip = _lead.funnelId?.alternativeAddress?.zipCode;
    const customerZip = _lead.funnelId?.customer?.zipCode;
    const leadZip = _lead.leadAddress?.zipCode || _lead.leadDetails?.leadZip;

    if (_lead.funnelId?.alternativeAddress?.enabled) {
      if ((alternativeZip || customerZip) && leadZip) {
        return false;
      }
    }
    if (customerZip && leadZip) {
      return false;
    }
    return true;
  };
  const renderCustomerInfo = (
    leadFunnel,
    objProperty,
    nestedObjProperty = ""
  ) => {
    const { alternativeAddress, customer } = leadFunnel;
    let info = "---";
    if (alternativeAddress?.enabled && alternativeAddress?.[objProperty]) {
      if (nestedObjProperty) {
        info = alternativeAddress[objProperty][nestedObjProperty];
      } else {
        info = alternativeAddress[objProperty];
      }
    } else if (customer?.[objProperty]) {
      if (nestedObjProperty) {
        info = customer[objProperty][nestedObjProperty];
      } else {
        info = customer[objProperty];
      }
    }
    return info;
  };
  const renderRestrictedCallTooltip = (lead) => {
    return availableAfterFlag ? (
      <div>
        <div>{`${t("leadAgent.available")} ${t("leadAgent.after")}`}:</div>
        <div>{generateBusinessDateTime(lead.availableAfter)}</div>
      </div>
    ) : contactLaterFlag ? (
      <div>
        <div>{t("leadAgent.contactLater")}:</div>
        <div>{generateBusinessDateTime(lead.contactLater)}</div>
      </div>
    ) : null;
  };
  const handleAnswerChange = (questionName, value) => {
    setResponses((prev) => {
      const updated = { ...prev };
      if (value.trim() === "") {
        delete updated[questionName];
      } else {
        updated[questionName] = value;
      }
      return updated;
    });
  };

  const getAppointmentTypeLabel = (type) => {
    switch (type) {
      case APPOINTMENT_TYPE.ONSITE_AT_LEAD_ADDRESS:
        return t("appointmentTypes.onSiteAtLeadAddress");
      case APPOINTMENT_TYPE.ONSITE_AT_COMPANY_ADDRESS:
        return t("appointmentTypes.onSiteAtCompanyAddress");
      case APPOINTMENT_TYPE.PHONE_APPOINTMENT:
        return t("appointmentTypes.phoneAppointment");
      default:
        return "---";
    }
  };

  const backToNormal = () => {
    setSelectedFunnel(null);
    setSearchType(SEARCH_LEAD_BY.PHONE);
    fetchAndAssignLeadToAgent({});
  };
  const handleAiLeadserviceTakeover = async () => {
    try {
      if (!lead._id || takeoverLoader) return;
      setTakeoverLoader(true);
      // Backend stops AI synchronously before returning the actionable lead.
      const response = await takeoverAiLeadserviceLead(lead._id);
      showSuccess(response.data.message);
      const updatedLead = response.data.data || {};
      setLead((prev) => ({
        ...prev,
        aiLeadserviceSent: false,
        aiLeadserviceTakenOver: true,
        aiLeadserviceTakenOverAt: updatedLead.aiLeadserviceTakenOverAt,
        aiLeadserviceTakenOverBy: updatedLead.aiLeadserviceTakenOverBy,
        notCallable: false,
        interactionStatus: LEAD_INTERACTION_STATUS.HANDLED,
        availableAfter: null,
        contactLater: null,
        serviceAgent: user._id,
        assignedVia: SEARCH_LEAD_BY.MANUAL_TAKEOVER
      }));
      setLeadSearchVia(SEARCH_LEAD_BY.MANUAL_TAKEOVER);
      setInteractionStatus(LEAD_INTERACTION_STATUS.HANDLED);
      setAvailableAfterFlag(false);
      setContactLaterFlag(false);
      await fetchLeadActivities(lead._id);
    } catch (error) {
      showError(
        error?.response?.data?.message ||
          t("leadAgent.aiLeadserviceTakeoverError")
      );
    } finally {
      setTakeoverLoader(false);
    }
  };

  useEffect(() => {
    clearInterval(timer.current);
    const updateLeadAgentTimestampHandler = async () => {
      try {
        await validateAndUpdateLeadAgentInfo(lead._id, {
          shouldAssignAgent: false
        });
      } catch (error) {
        console.log(error);
        if (error.response?.data?.data?.isAlreadyTaken) {
          showError(t("leadAgent.alreadyTakenMsg"));
          navigate(0);
        } else {
          showError(error.response?.data?.message);
        }
      }
    };
    if (lead?._id) {
      timer.current = setInterval(() => {
        updateLeadAgentTimestampHandler();
      }, LEAD_AGENT_TIMESTAMP_UPDATE_DELAY);
    } else {
      clearInterval(timer.current);
    }
    // eslint-disable-next-line
  }, [lead, navigate]);
  useEffect(() => {
    fetchAndAssignLeadToAgent({});
    return () => {
      setComment("");
      setSystemGeneratedBudget("");
      setResponses({});
      setIsLeadServicePricePromptRequired(false);
      setIsLeadServicePricePromptAnswered(true);
      resetAutopilotInput();
      clearInterval(timer.current);
    };
  }, [fetchAndAssignLeadToAgent, resetAutopilotInput]);

  useEffect(() => {
    if (!lead?._id) return undefined;
    const isDirectLeadSearch = [
      SEARCH_LEAD_BY.PHONE,
      SEARCH_LEAD_BY.EMAIL
    ].includes(leadSearchVia);
    return registerAgentAssignment({
      key: `agent-${lead._id}`,
      route: "agent",
      isAssigned: !isDirectLeadSearch,
      isInCall: isAgentInCall,
      releaseAssignment: async () => {
        if (isDirectLeadSearch) return;
        clearInterval(timer.current);
        await unassignLeadFromAgent(lead._id);
      }
    });
  }, [isAgentInCall, lead?._id, leadSearchVia, registerAgentAssignment]);

  useBlockInCallNavigation({
    when: isAgentInCall,
    allowedPathPrefix: "/dashboard/agent",
    onBlocked: () => showError(t("agentRouteSwitch.activeCallError")),
    unloadMessage: t("agentRouteSwitch.activeCallError")
  });

  return (
    <>
      <FormProvider {...methods}>
        <PreventReload message="Please mark the result of the call" />
        <div className="d-flex justify-content-end my-3">
          <Col xs={12} sm={8} md={5} lg={3}>
            <InputGroup>
              <SplitButton
                id="search-by-lead-info"
                title={searchType.toLowerCase()}
                bsPrefix={"text-capitalize btn btn-outline-secondary"}
                disabled={isAgentInCall}
              >
                {Object.keys(leadSearchOptions).map((type, idx) => (
                  <DropdownItem
                    key={idx}
                    className="text-capitalize"
                    disabled={isAgentInCall}
                    onClick={() => onSelectSearchType(type)}
                  >
                    {type.toLowerCase()}
                  </DropdownItem>
                ))}
              </SplitButton>
              <SelectContainer
                isClearable
                className="flex-fill"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: "0 4px 4px 0",
                    borderColor: "var(--field-border-color-regular)"
                  }),
                  // Fixes the overlapping problem of the component
                  menu: (provided) => ({ ...provided, zIndex: 1021 })
                }}
                placeholder={`${t("leadAgent.searchBy")}...`}
                isLoading={searchLoader.current}
                isDisabled={isAgentInCall}
                onChange={(option, action) => {
                  if (isAgentInCall) return;
                  if (action?.action === "select-option") {
                    if (searchType === SEARCH_LEAD_BY.FUNNEL) {
                      setSelectedFunnel(option);
                      fetchAndAssignLeadToAgent({
                        funnelId: option._id,
                        searchType
                      });
                    } else if (
                      searchType === SEARCH_LEAD_BY.EMAIL ||
                      searchType === SEARCH_LEAD_BY.PHONE
                    ) {
                      fetchAndAssignLeadToAgent({
                        leadId: option._id,
                        searchType
                      });
                    }
                  }
                }}
                components={{
                  DropdownIndicator: () => null,
                  IndicatorSeparator: () => null,
                  LoadingIndicator: () => null,
                  Option,
                  MenuList,
                  MenuListFooter: (
                    <MenuListFooter
                      showing={options?.length}
                      total={paginateOptions.totalDocs}
                      onClick={() => {
                        searchLoader.current = true;
                        pageIndex.current = pageIndex.current + 1;
                        if (searchType === SEARCH_LEAD_BY.FUNNEL) {
                          fetchFunnelOptions(inputValue);
                        } else if (
                          searchType === SEARCH_LEAD_BY.EMAIL ||
                          searchType === SEARCH_LEAD_BY.PHONE
                        ) {
                          fetchLeadOptions(inputValue);
                        }
                      }}
                      isDisabled={
                        interactionStatus === LEAD_INTERACTION_STATUS.INITIATED
                      }
                    />
                  )
                }}
                isOptionDisabled={(option) => option.disabled}
                getOptionLabel={(option) => generateOptLabelString(option)}
                getOptionValue={(option) => option._id}
                inputValue={inputValue}
                value={inputValue}
                options={options}
                onInputChange={(inputValue, { action }) =>
                  handleInputChange(action, inputValue)
                }
              />
            </InputGroup>
          </Col>
        </div>
        <AnimationContainer key={lead?._id} className="mb-4">
          {!isLoading ? (
            lead?._id ? (
              <CustomCard className="p-4">
                {isAiLeadserviceClosedLead && (
                  <div className="alert alert-info d-flex align-items-center justify-content-between gap-3">
                    <div>
                      <div className="fw-bold">
                        <FontAwesomeIcon icon={faRobot} className="me-2" />
                        {t("leadAgent.aiLeadserviceClosedTitle")}
                      </div>
                      <div>{t("leadAgent.aiLeadserviceClosedMessage")}</div>
                    </div>
                    <div>
                      <Button
                        size="sm"
                        variant="primary"
                        className="flex-shrink-0 me-2"
                        disabled={takeoverLoader}
                        onClick={handleAiLeadserviceTakeover}
                      >
                        {takeoverLoader
                          ? t("leadAgent.aiLeadserviceTakingOver")
                          : t("leadAgent.aiLeadserviceTakeover")}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="flex-shrink-0"
                        disabled={takeoverLoader}
                        onClick={backToNormal}
                      >
                        {t("leadAgent.aiLeadserviceClosedNext")}
                      </Button>
                    </div>
                  </div>
                )}
                <Row>
                  <Col md={12} lg={6}>
                    <form>
                      <CustomDiv className="mb-4 position-relative">
                        {leadSearchVia !== SEARCH_LEAD_BY.AUTO &&
                          leadSearchVia !== SEARCH_LEAD_BY.APPOINTMENT && (
                            <AbsoluteInfoContainer>
                              <ExtraInfoCustomBadge className="position-absolute bg-primary text-white fw-bold text-center">
                                {`${t("leadAgent.searchBy")}: ${leadSearchVia}`}
                              </ExtraInfoCustomBadge>
                            </AbsoluteInfoContainer>
                          )}
                        <div className="d-flex mb-2">
                          <div className="d-flex align-items-center text-break">
                            {editMode.leadName ? (
                              <Form.Group>
                                <Form.Control
                                  required
                                  placeholder={t("leadAgent.enterValue")}
                                  {...register("leadName")}
                                  isInvalid={errors?.leadName}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors?.leadName?.message}
                                </Form.Control.Feedback>
                              </Form.Group>
                            ) : (
                              <>
                                <h3 className="fw-bold mb-0">
                                  {leadData.leadName}
                                </h3>
                                {isUnbookedAndNotSentToAiLeadService && (
                                  <Button
                                    variant="outlined"
                                    disabled={isQualified}
                                    className="text-secondary border-0"
                                    onClick={() => handleEditMode("leadName")}
                                  >
                                    <FontAwesomeIcon icon={faPen} />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                          <div className="ms-4 d-flex align-self-center">
                            <CustomBadge
                              className="px-3 py-2 d-flex align-items-center fw-normal"
                              $btnbg={`--${lead.leadDetails?.leadType?.toLowerCase()}-badge-bg`}
                              $btncolor={`--${lead.leadDetails?.leadType?.toLowerCase()}-color`}
                            >
                              {lead.leadDetails?.leadType?.toLowerCase()}
                            </CustomBadge>
                          </div>
                          <div className="ms-3 align-self-center">
                            <CustomBadge className="p-2 bg-light text-dark fw-normal border">
                              {isQualified ? (
                                <>
                                  <FontAwesomeIcon
                                    className="text-success me-2"
                                    icon={faCircleCheck}
                                  />
                                  {t("leadAgent.successfullyQualified")}
                                </>
                              ) : (
                                <>
                                  <FontAwesomeIcon
                                    className="text-danger me-2"
                                    icon={faXmarkCircle}
                                  />
                                  {t("leadAgent.notQualifiedYet")}
                                </>
                              )}
                            </CustomBadge>
                          </div>
                          {(!lead.notCallable || lead.appointmentOn) && (
                            <div className="ms-3 align-self-center">
                              <CustomBadge className="p-2 bg-light border text-dark fw-normal">
                                {lead.availableAfter &&
                                checkLeadAvailability(lead.availableAfter) ? (
                                  <AvailabilityBadgeContent
                                    icon={faClock}
                                    generator={generateBusinessDateTime}
                                    dateValue={lead.availableAfter}
                                    tooltipText={`${t(
                                      "leadAgent.available"
                                    )} ${t("leadAgent.after")}`}
                                  />
                                ) : shouldContactLater(lead.contactLater) ? (
                                  <AvailabilityBadgeContent
                                    icon={faCalendarDays}
                                    generator={generateBusinessDateTime}
                                    dateValue={lead.contactLater}
                                    tooltipText={t("leadAgent.contactLater")}
                                  />
                                ) : (
                                  <>{t("leadAgent.available")}!</>
                                )}
                              </CustomBadge>
                            </div>
                          )}
                        </div>
                        <div className="d-flex">
                          <div className="align-self-center text-break">
                            ID: {lead._id}
                          </div>
                          <DotSeparator></DotSeparator>
                          <div className="align-self-center">
                            {generateDateTime(lead.createdAt)} Uhr
                          </div>
                          <DotSeparator></DotSeparator>
                          {editMode.leadEmail ? (
                            <Form.Group>
                              <Form.Control
                                required
                                placeholder={t("leadAgent.enterValue")}
                                {...register("leadEmail")}
                                isInvalid={errors?.leadEmail}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors?.leadEmail?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          ) : (
                            <div className="text-break d-flex align-items-center justify-content-between">
                              <span>{leadData.leadEmail}</span>
                              {isUnbookedAndNotSentToAiLeadService && (
                                <Button
                                  size="sm"
                                  variant="outlined"
                                  disabled={isQualified}
                                  className="text-secondary border-0 pe-0"
                                  onClick={() => handleEditMode("leadEmail")}
                                >
                                  <FontAwesomeIcon icon={faPen} />
                                </Button>
                              )}
                            </div>
                          )}
                          <DotSeparator></DotSeparator>
                          {editMode.leadPhone ? (
                            <Form.Group>
                              <Form.Control
                                required
                                placeholder={t("leadAgent.enterValue")}
                                {...register("leadPhone")}
                                isInvalid={errors?.leadPhone}
                              />

                              <Form.Control.Feedback type="invalid">
                                {errors?.leadPhone?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          ) : (
                            <div className="text-break d-flex align-items-center justify-content-between">
                              {generatePhoneNumber(leadData.leadPhone)}
                              {isUnbookedAndNotSentToAiLeadService && (
                                <Button
                                  size="sm"
                                  variant="outlined"
                                  onClick={() => {
                                    setValue(
                                      "leadPhone",
                                      generatePhoneNumber(leadData.leadPhone),
                                      { shouldDirty: true }
                                    );
                                    handleEditMode("leadPhone");
                                  }}
                                  className="border-0 text-secondary"
                                  disabled={isQualified}
                                >
                                  <FontAwesomeIcon icon={faPen} />
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                        <EditCollapsableActions
                          shouldCollapseIn={
                            isEditModeEnabled &&
                            isUnbookedAndNotSentToAiLeadService
                          }
                          cancelEdit={cancelEdit}
                          isDisabled={updateLoader || isQualified}
                          updateLoader={updateLoader}
                          handleUpdateLead={handleUpdateLead}
                        />
                      </CustomDiv>
                      <CustomDiv className="mb-4 py-4 position-relative">
                        <Row>
                          <AddressColumn xs={6}>
                            <h5 className="fw-bold mb-0">
                              {lead.funnelId?.companyNameForLeadAgent ||
                                (lead.reclaimed &&
                                  lead.companyInfo?.companyName) ||
                                lead.funnelId?.companyInfo?.companyName ||
                                lead.funnelId?.customer?.companyName ||
                                "---"}
                            </h5>
                            <div>
                              <div>
                                {renderCustomerInfo(lead.funnelId, "address")}
                              </div>
                              <span className="me-1">
                                {renderCustomerInfo(lead.funnelId, "zipCode")},
                              </span>
                              <span className="text-capitalize">
                                {renderCustomerInfo(
                                  lead.funnelId,
                                  "city",
                                  "name"
                                )}
                              </span>
                              <div>
                                {renderCustomerInfo(lead?.funnelId, "country")}
                              </div>
                            </div>
                            {hasAccess(ACCESS_CONFIG.ADMIN_TIER) &&
                            emailsToSend.length ? (
                              <div className="mt-2">
                                <div className="d-inline-block fw-bold mb-1 fs-6 border-dark border-bottom">
                                  {t("leadAgent.emailsToSendHeading")}:
                                </div>
                                <div>
                                  {emailsToSend.map((email, idx) => (
                                    <div key={idx} className="ms-1 mb-1">
                                      {email.emailAddress}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <></>
                            )}
                          </AddressColumn>
                          <Col xs={6} className="py-1 px-3">
                            <AbsoluteInfoContainer>
                              {checkIfZipCodeIsMissing(lead) && (
                                <ExtraInfoCustomBadge className="position-absolute bg-danger text-white fw-bold text-center">
                                  {t("leadAgent.postalCodeMissingMsg")}
                                </ExtraInfoCustomBadge>
                              )}
                              <MapLink
                                href={generateGoogleMapLink(lead)}
                                target="_blank"
                                rel="noreferrer"
                                className={`d-flex align-items-center text-decoration-none text-dark ${
                                  checkIfZipCodeIsMissing(lead)
                                    ? "pe-none text-muted"
                                    : ""
                                }`}
                              >
                                <FontAwesomeIcon
                                  size="xl"
                                  icon={faLocationDot}
                                  className="me-2 text-secondary"
                                />
                                <CalcDistanceText
                                  className={`fw-bold ${
                                    checkIfZipCodeIsMissing(lead)
                                      ? "text-muted"
                                      : "text-dark"
                                  }`}
                                >
                                  {t("leadAgent.calcDistance")}
                                </CalcDistanceText>
                              </MapLink>
                            </AbsoluteInfoContainer>
                            <CustomFontSizeDiv
                              $fs="12"
                              className="text-secondary fw-bold"
                            >
                              {lead.leadDetails?.leadType ===
                              FUNNEL_TYPE_FILTER.EMPLOYEE
                                ? t("common.job").toUpperCase()
                                : t("common.product").toUpperCase()}
                            </CustomFontSizeDiv>
                            <CustomFontSizeDiv $fs="18" className="fw-bold">
                              {lead.funnelId?.leadServiceProductName ||
                                lead.funnelId?.category?.name}
                            </CustomFontSizeDiv>

                            {leadServiceIndividualNote && (
                              <div className="my-3">
                                <CustomFontSizeDiv
                                  $fs="12"
                                  className="text-secondary fw-bold"
                                >
                                  {t(
                                    "funnelIntegration.leadService.individualNoteLabel"
                                  )}
                                </CustomFontSizeDiv>
                                <CustomFontSizeDiv $fs="18" className="fw-bold">
                                  {leadServiceIndividualNote}
                                </CustomFontSizeDiv>
                              </div>
                            )}

                            <InterviewSummary interview={lead.interview} />

                            {lead.appointmentOn && (
                              <>
                                <CustomFontSizeDiv
                                  $fs="12"
                                  className="text-secondary fw-bold"
                                >
                                  {t("common.appointmentDetail").toUpperCase()}
                                </CustomFontSizeDiv>
                                <div className="d-flex align-items-center mb-2">
                                  {editMode.appointmentOn ? (
                                    <Form.Group>
                                      <Form.Control
                                        required
                                        type="datetime-local"
                                        {...register("appointmentOn")}
                                        isInvalid={errors?.appointmentOn}
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        {errors?.appointmentOn?.message}
                                      </Form.Control.Feedback>
                                    </Form.Group>
                                  ) : (
                                    <>
                                      <CustomFontSizeDiv
                                        $fs="18"
                                        className="fw-bold"
                                      >
                                        {generateBusinessDateTime(
                                          lead.appointmentOn,
                                          DATE_TIME_WITH_MONTH_FORMAT
                                        ) || t("leadAgent.noAppointment")}
                                      </CustomFontSizeDiv>
                                      {hasAccess(ACCESS_CONFIG.ADMIN_TIER) &&
                                        !isAiLeadserviceClosedLead && (
                                          <Button
                                            size="sm"
                                            variant="outlined"
                                            className="text-secondary border-0 ms-2"
                                            onClick={() =>
                                              handleEditMode("appointmentOn")
                                            }
                                          >
                                            <FontAwesomeIcon icon={faPen} />
                                          </Button>
                                        )}
                                    </>
                                  )}
                                </div>
                                <CustomFontSizeDiv
                                  $fs="12"
                                  className="text-secondary fw-bold"
                                >
                                  {t("leadAgent.appointmentType").toUpperCase()}
                                </CustomFontSizeDiv>
                                <CustomFontSizeDiv $fs="18" className="fw-bold">
                                  {getAppointmentTypeLabel(
                                    lead.appointmentType
                                  )}
                                </CustomFontSizeDiv>
                              </>
                            )}
                          </Col>
                        </Row>
                        <EditCollapsableActions
                          shouldCollapseIn={
                            isEditModeEnabled &&
                            Boolean(lead.appointmentOn) && // added Boolean to avoid console invalid prop error
                            !isAiLeadserviceClosedLead
                          }
                          cancelEdit={cancelEdit}
                          isDisabled={updateLoader}
                          updateLoader={updateLoader}
                          handleUpdateLead={handleUpdateLead}
                        />
                      </CustomDiv>
                    </form>
                    {!isAiLeadserviceClosedLead && (
                      <CustomDiv>
                        <Row>
                          <Col xs={6} className="align-self-center">
                            {interactionStatus !==
                            LEAD_INTERACTION_STATUS.INITIATED ? (
                              <div className="text-center">
                                {(availableAfterFlag || contactLaterFlag) && (
                                  <OverlayTrigger
                                    overlay={
                                      <Tooltip>
                                        {renderRestrictedCallTooltip(lead)}
                                      </Tooltip>
                                    }
                                  >
                                    {/* ForceCallContainer */}
                                    <div
                                      className="me-2 cursor-pointer text-decoration-underline"
                                      onClick={() => {
                                        setContactLaterFlag(false);
                                        setAvailableAfterFlag(false);
                                      }}
                                    >
                                      <div>
                                        <FontAwesomeIcon
                                          className="me-1 text-danger"
                                          icon={faBellSlash}
                                        />
                                        {t("leadAgent.callAnyway")}?
                                      </div>
                                    </div>
                                  </OverlayTrigger>
                                )}

                                <ActivityButton
                                  activityHandler={activityHandler}
                                  currentActivityLoader={currentActivityLoader}
                                  ACTIVITY_TYPE={ACTIVITY_TYPES.CALL}
                                  ACTIVITY_RESULT_TYPE={
                                    lead.appointmentOn ? APPOINTMENT_CALL : CALL
                                  }
                                  isDisabled={
                                    areLeadActionsDisabled ||
                                    availableAfterFlag ||
                                    contactLaterFlag
                                  }
                                  btnText={t("leadAgent.calling")}
                                  btnStyle="btn-success w-100"
                                  icon={faPhone}
                                  btnTextPrimary={
                                    interactionStatus ===
                                      LEAD_INTERACTION_STATUS.INITIATED ||
                                    activityLoader
                                      ? t("leadAgent.callInProgress")
                                      : t("leadAgent.callContact")
                                  }
                                  className="btn btn-success text-white"
                                />
                                <a
                                  hidden
                                  ref={phoneNumberClickRef}
                                  href={`tel:${generatePhoneNumber(
                                    leadData.leadPhone
                                  )}`}
                                >
                                  <></>
                                </a>

                                {lead.funnelId?.companyInfo?.country !==
                                  COUNTRY_GERMANY && (
                                  <CountryFlagEmoji
                                    fs="26"
                                    countryName={
                                      lead.funnelId?.companyInfo?.country
                                    }
                                  />
                                )}
                              </div>
                            ) : lead.appointmentOn ? (
                              // Activity Results for appointment call confirmation
                              <div>
                                <Row>
                                  <Col className="pe-0">
                                    <ActivityButton
                                      ACTIVITY_TYPE={ACTIVITY_TYPES.CALL}
                                      activityHandler={activityHandler}
                                      currentActivityLoader={
                                        currentActivityLoader
                                      }
                                      ACTIVITY_RESULT_TYPE={
                                        APPOINTMENT_NOT_REACHED
                                      }
                                      isDisabled={
                                        interactionStatus !==
                                          LEAD_INTERACTION_STATUS.INITIATED ||
                                        areLeadActionsDisabled
                                      }
                                      btnText={t("leadAgent.sending")}
                                      btnStyle="btn-danger w-100"
                                      icon={faPersonCircleExclamation}
                                      btnTextPrimary={t("leadAgent.notReached")}
                                      variant="danger"
                                      className="text-start mb-2 w-100"
                                    />
                                  </Col>
                                  <Col className="pe-0">
                                    <ActivityButton
                                      ACTIVITY_TYPE={ACTIVITY_TYPES.CALL}
                                      activityHandler={activityHandler}
                                      currentActivityLoader={
                                        currentActivityLoader
                                      }
                                      ACTIVITY_RESULT_TYPE={
                                        APPOINTMENT_CANCELLED
                                      }
                                      isDisabled={
                                        interactionStatus !==
                                          LEAD_INTERACTION_STATUS.INITIATED ||
                                        areLeadActionsDisabled
                                      }
                                      btnText={t("leadAgent.cancelling")}
                                      btnStyle="btn-danger w-100"
                                      icon={faXmark}
                                      btnTextPrimary={t("leadAgent.cancelled")}
                                      variant="danger"
                                      comment={t("appointmentNote.cancel")}
                                      className="text-start mb-2 w-100"
                                    />
                                  </Col>
                                </Row>
                                <Row>
                                  <Col className="pe-0">
                                    <ActivityButton
                                      ACTIVITY_TYPE={ACTIVITY_TYPES.CALL}
                                      activityHandler={activityHandler}
                                      currentActivityLoader={
                                        currentActivityLoader
                                      }
                                      ACTIVITY_RESULT_TYPE={
                                        APPOINTMENT_CONFIRMED
                                      }
                                      isDisabled={
                                        interactionStatus !==
                                          LEAD_INTERACTION_STATUS.INITIATED ||
                                        areLeadActionsDisabled
                                      }
                                      btnText={t("leadAgent.confirming")}
                                      btnStyle="btn-success w-100"
                                      icon={faPhone}
                                      btnTextPrimary={t("leadAgent.confirmed")}
                                      variant="success"
                                      comment={`${t(
                                        "appointmentNote.confirm"
                                      )}${generateBusinessDateTime(
                                        lead.appointmentOn,
                                        DATE_TIME_FORMAT
                                      )}`}
                                      className="text-start mb-2 w-100"
                                    />
                                  </Col>
                                  <Col className="pe-0">
                                    <Button
                                      as={"a"} // anchor
                                      target="_blank"
                                      rel="noreferrer"
                                      variant="secondary"
                                      disabled={
                                        interactionStatus !==
                                          LEAD_INTERACTION_STATUS.INITIATED ||
                                        areLeadActionsDisabled
                                      }
                                      className="text-start w-100"
                                      style={{ fontSize: "14px" }}
                                      href={
                                        lead.funnelId?.customerAppointmentLink
                                      }
                                      onClick={() => {
                                        toggleAppointmentDialog();
                                      }}
                                    >
                                      <FontAwesomeIcon
                                        className="ms-1 me-2"
                                        icon={faCalendarDays}
                                      />
                                      {t("leadAgent.postponed")}
                                    </Button>
                                  </Col>
                                </Row>
                              </div>
                            ) : (
                              // Activity Results for normal/usual call confirmation
                              <div>
                                <Row>
                                  <Col className="pe-0" sm={12} md={6}>
                                    <ActivityButton
                                      ACTIVITY_TYPE={ACTIVITY_TYPES.CALL}
                                      activityHandler={activityHandler}
                                      currentActivityLoader={
                                        currentActivityLoader
                                      }
                                      ACTIVITY_RESULT_TYPE={NOT_REACHED}
                                      isDisabled={
                                        interactionStatus !==
                                          LEAD_INTERACTION_STATUS.INITIATED ||
                                        areLeadActionsDisabled
                                      }
                                      btnText={t("leadAgent.sending")}
                                      btnStyle="btn-danger w-100"
                                      icon={faPersonCircleExclamation}
                                      btnTextPrimary={t("leadAgent.notReached")}
                                      variant="danger"
                                      className="text-start mb-2 w-100"
                                    />
                                  </Col>
                                  <Col className="pe-0" sm={12} md={6}>
                                    <AgentViewActivityBtn
                                      variant="secondary"
                                      className="text-start w-100"
                                      disabled={areLeadActionsDisabled}
                                      onClick={togglePickerDialog}
                                    >
                                      <FontAwesomeIcon
                                        className="ms-1 me-2"
                                        icon={faCalendarDays}
                                      />
                                      {t("leadAgent.contactLater")}
                                    </AgentViewActivityBtn>
                                  </Col>
                                  <Col className="pe-0" sm={12} md={6}>
                                    <ActivityButton
                                      ACTIVITY_TYPE={ACTIVITY_TYPES.CALL}
                                      activityHandler={activityHandler}
                                      currentActivityLoader={
                                        currentActivityLoader
                                      }
                                      ACTIVITY_RESULT_TYPE={NUMBER_WRONG}
                                      isDisabled={
                                        interactionStatus !==
                                          LEAD_INTERACTION_STATUS.INITIATED ||
                                        areLeadActionsDisabled
                                      }
                                      btnText={t("leadAgent.sending")}
                                      btnStyle="btn-warning w-100"
                                      icon={faPhoneSlash}
                                      btnTextPrimary={t("leadAgent.wrongNum")}
                                      variant="warning"
                                      className="text-start mb-2 w-100"
                                    />
                                  </Col>
                                  <Col className="pe-0" sm={12} md={6}>
                                    <ActivityButton
                                      ACTIVITY_TYPE={ACTIVITY_TYPES.CALL}
                                      activityHandler={activityHandler}
                                      currentActivityLoader={
                                        currentActivityLoader
                                      }
                                      ACTIVITY_RESULT_TYPE={NAME_WRONG}
                                      isDisabled={
                                        interactionStatus !==
                                          LEAD_INTERACTION_STATUS.INITIATED ||
                                        areLeadActionsDisabled
                                      }
                                      btnText={t("leadAgent.sending")}
                                      btnStyle="btn-warning w-100"
                                      icon={faUser}
                                      btnTextPrimary={t("leadAgent.wrongName")}
                                      variant="warning"
                                      className="text-start mb-2 w-100"
                                    />
                                  </Col>
                                </Row>
                              </div>
                            )}
                          </Col>
                          {(isAutopilotLead || !lead.appointmentOn) && (
                            <Col xs={6}>
                              <CustomFontSizeDiv
                                $fs="12"
                                className="text-secondary fw-bold"
                              >
                                {t("leadAgent.notes")}
                              </CustomFontSizeDiv>
                              <div>
                                {/* only for display system generated budget */}
                                {systemGeneratedBudget && (
                                  <Form.Control
                                    type="text"
                                    value={systemGeneratedBudget}
                                    className="mt-1 px-3 rounded-0 border-bottom-0 rounded-top bg-white"
                                    disabled
                                  />
                                )}
                                <NotesTextArea
                                  as="textarea"
                                  className={`p-3 w-100 ${
                                    systemGeneratedBudget
                                      ? "border-top-0 rounded-0 rounded-bottom pt-0"
                                      : "mt-1 rounded"
                                  }`}
                                  onChange={({ target }) =>
                                    setComment(target.value)
                                  }
                                  placeholder={t("leadAgent.notesPlaceholder")}
                                  value={comment}
                                  disabled={
                                    interactionStatus !==
                                      LEAD_INTERACTION_STATUS.INITIATED ||
                                    areLeadActionsDisabled
                                  }
                                />
                              </div>
                              {interactionStatus ===
                                LEAD_INTERACTION_STATUS.INITIATED && (
                                <>
                                  <Row className="mt-2">
                                    <Col hidden={isAutopilotLead}>
                                      <ActivityButton
                                        activityHandler={activityHandler}
                                        currentActivityLoader={
                                          currentActivityLoader
                                        }
                                        ACTIVITY_TYPE={ACTIVITY_TYPES.CALL}
                                        ACTIVITY_RESULT_TYPE={QUALIFIED}
                                        isDisabled={
                                          isAutopilotLead ||
                                          interactionStatus !==
                                            LEAD_INTERACTION_STATUS.INITIATED ||
                                          areLeadActionsDisabled ||
                                          !canQualifyLead
                                        }
                                        variant="success"
                                        btnText={t("leadAgent.sending")}
                                        btnStyle="btn-success flex-fill w-100"
                                        btnTextPrimary={t(
                                          "leadAgent.qualified"
                                        )}
                                        comment={
                                          systemGeneratedBudget
                                            ? `${systemGeneratedBudget} ${comment}`
                                            : comment
                                        }
                                        responses={responses}
                                        className="w-100"
                                        funnelAppointment={funnelAppointment}
                                        appointmentDialog={
                                          toggleAppointmentDialog
                                        }
                                        disabledTooltip={
                                          hasMissingMandatoryLeadServiceAnswers
                                            ? t(
                                                "leadAgent.mandatoryLeadServiceAnswersTooltip"
                                              )
                                            : ""
                                        }
                                      />
                                    </Col>
                                    <Col>
                                      <ActivityButton
                                        activityHandler={activityHandler}
                                        currentActivityLoader={
                                          currentActivityLoader
                                        }
                                        ACTIVITY_TYPE={ACTIVITY_TYPES.CALL}
                                        ACTIVITY_RESULT_TYPE={NOT_SUITABLE}
                                        isDisabled={
                                          interactionStatus !==
                                            LEAD_INTERACTION_STATUS.INITIATED ||
                                          !comment ||
                                          areLeadActionsDisabled
                                        }
                                        variant="danger"
                                        btnText={t("leadAgent.sending")}
                                        btnStyle="btn-danger flex-fill w-100"
                                        btnTextPrimary={t(
                                          "leadAgent.notSuitable"
                                        )}
                                        comment={
                                          systemGeneratedBudget
                                            ? `${systemGeneratedBudget} ${comment}`
                                            : comment
                                        }
                                        className="w-100"
                                      />
                                    </Col>
                                  </Row>
                                  <Row>
                                    {showCustomerAppointmentLink && (
                                      <Col>
                                        <Card
                                          bg="warning"
                                          className="mt-3 p-4 border-0"
                                        >
                                          <h6 className="text-center">
                                            {t(
                                              "leadAgent.appointmentWitHtheCustomer"
                                            )}
                                          </h6>
                                          <HorizontalDivider />
                                          <Button
                                            as={"a"} // anchor
                                            target="_blank"
                                            rel="noreferrer"
                                            href={
                                              lead.funnelId
                                                ?.customerAppointmentLink
                                            }
                                            disabled={isQualified}
                                          >
                                            {t(
                                              "leadAgent.appointmentInCalendy"
                                            )}
                                          </Button>
                                          <h6 className="text-center mt-3">
                                            {t("leadAgent.appointmentType")} :{" "}
                                            <span className="fw-bold">
                                              {getAppointmentTypeLabel(
                                                lead.funnelId?.appointmentType
                                              )}
                                            </span>
                                          </h6>
                                        </Card>
                                      </Col>
                                    )}
                                  </Row>
                                </>
                              )}
                            </Col>
                          )}
                        </Row>
                      </CustomDiv>
                    )}
                    {isUnbookedAndNotSentToAiLeadService && (
                      <>
                        {lead.funnelId?.leadServicePlus &&
                          !isAutopilotLead &&
                          interactionStatus ===
                            LEAD_INTERACTION_STATUS.INITIATED && (
                            <AgentPricePrompt
                              setCommentPrice={setCommentPrice}
                              leadFunnel={lead.funnelId}
                              onCompletionChange={
                                handleLeadServicePricePromptCompletion
                              }
                            />
                          )}

                        {isAutopilotLead && (
                          <AutopilotCard
                            lead={lead}
                            callStarted={
                              interactionStatus ===
                              LEAD_INTERACTION_STATUS.INITIATED
                            }
                            disabled={areLeadActionsDisabled}
                            comment={comment}
                            searchVia={leadSearchVia}
                            onAnswersChange={setAutopilotAnswers}
                            onBudgetChange={setAutopilotBudget}
                            onBooked={handleAutopilotBooked}
                            onBookingChange={setIsAutopilotBooking}
                          />
                        )}
                        {isAutopilotEndedLead && (
                          <AutopilotCard lead={lead} readOnly />
                        )}
                        {showLeadServiceQuestions && (
                          <CustomDiv className="mt-4">
                            {lead.funnelId.leadServiceQuestions.map(
                              (question, index) => (
                                <div className="mb-2" key={index}>
                                  <Form.Label className="text-secondary mb-0">
                                    {question.name}
                                  </Form.Label>
                                  <Form.Control
                                    type="text"
                                    className="mt-1"
                                    onChange={({ target }) =>
                                      handleAnswerChange(
                                        question.name,
                                        target.value
                                      )
                                    }
                                    placeholder={t("leadAgent.answer")}
                                    value={responses[question.name] || ""}
                                    disabled={
                                      interactionStatus !==
                                        LEAD_INTERACTION_STATUS.INITIATED ||
                                      areLeadActionsDisabled
                                    }
                                  />
                                </div>
                              )
                            )}
                          </CustomDiv>
                        )}
                      </>
                    )}
                  </Col>
                  <Col md={12} lg={2} className="mt-4 mt-lg-0">
                    <CustomCard>
                      <ControlledHeightContainer>
                        <CustomCardHeader className="sticky-top bg-light fw-bold">
                          {t("leadActivities.title")}
                        </CustomCardHeader>
                        {isLoadingActivities ? (
                          <ControlledHeightLoaderContainer className="d-flex align-items-center justify-content-center">
                            <Loader />
                          </ControlledHeightLoaderContainer>
                        ) : (
                          <div className="p-3">
                            <LeadActivities activities={activities} />
                          </div>
                        )}
                      </ControlledHeightContainer>
                    </CustomCard>
                  </Col>
                  <Col md={12} lg={4} className="mt-4 mt-lg-0">
                    <CustomCard>
                      <ControlledHeightContainer>
                        <CustomCardHeader className="sticky-top bg-light fw-bold">
                          {t("script")}
                        </CustomCardHeader>
                        <div className="p-2">
                          {lead.reclaimed ? (
                            // Filtered Lead Script
                            <PDFViewer
                              objHeight="650"
                              ariaLabel="ap"
                              scriptUrl={generateScriptUrl({
                                workspace: user.workspace,
                                scriptProp: SCRIPT_WORKSPACE_PATH.RECLAIM_LEADS,
                                localFileName:
                                  SCRIPT_LOCAL_FILE_NAME.RECLAIM_LEADS
                              })}
                            />
                          ) : lead.appointmentOn ? (
                            // Appointment Script
                            <PDFViewer
                              objHeight="650"
                              ariaLabel="ap"
                              scriptUrl={generateScriptUrl({
                                workspace: user.workspace,
                                scriptProp: SCRIPT_WORKSPACE_PATH.AP,
                                localFileName: SCRIPT_LOCAL_FILE_NAME.AP
                              })}
                            />
                          ) : lead.funnelId?.leadServicePlus ? (
                            // LS+ Script
                            <PDFViewer
                              objHeight="650"
                              ariaLabel="ls-plus"
                              scriptUrl={generateScriptUrl({
                                workspace: user.workspace,
                                scriptProp: SCRIPT_WORKSPACE_PATH.LS, // mistake but it works can't change
                                localFileName: SCRIPT_LOCAL_FILE_NAME.LS
                              })}
                            />
                          ) : (
                            <PDFViewer
                              objHeight="650"
                              ariaLabel="default"
                              scriptUrl={generateScriptUrl({
                                workspace: user.workspace,
                                scriptProp: SCRIPT_WORKSPACE_PATH.LS_PLUS, // mistake but it works can't change
                                localFileName: SCRIPT_LOCAL_FILE_NAME.LS_PLUS
                              })}
                            />
                          )}
                        </div>
                      </ControlledHeightContainer>
                    </CustomCard>
                  </Col>
                </Row>
              </CustomCard>
            ) : searchType === SEARCH_LEAD_BY.FUNNEL && selectedFunnel ? (
              <div className="text-center fs-6">
                <Trans
                  i18nKey={
                    lead?.appointmentOn
                      ? t("leadAgent.noAppointmentForFunnel", {
                          funnelName: selectedFunnel.general?.funnelName
                        })
                      : t("leadAgent.allOpenLeadsContactedMsg", {
                          funnelName: selectedFunnel.general?.funnelName
                        })
                  }
                />
                <div className="mt-2">
                  <Button size="sm" onClick={backToNormal}>
                    <FontAwesomeIcon icon={faRotateLeft} className="me-2" />
                    {t("leadAgent.backToNormalBtn")}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-5 text-center fs-4">
                <div>{t("leadAgent.noLeads")}</div>
              </div>
            )
          ) : (
            <Loader />
          )}
        </AnimationContainer>
      </FormProvider>
      <PickerDialog
        show={showPickerDialog}
        onHide={togglePickerDialog}
        activityHandler={activityHandler}
        hint={isAutopilotLead ? t("leadAgent.autopilot.laterHint") : ""}
      />
      <AppointmentPickerDialog
        show={showAppointmentDialog}
        onHide={toggleAppointmentDialog}
        activityHandler={activityHandler}
        allowClose={lead?.appointmentOn ? true : false}
        comment={
          systemGeneratedBudget
            ? `${systemGeneratedBudget} ${comment}`
            : comment
        }
        responses={responses}
        forLS={lead?.appointmentOn ? false : true}
        funnelAppointmentType={
          lead?.appointmentOn ? null : lead?.funnelId?.appointmentType
        }
      />
    </>
  );
};

export default LeadAgentPanelView;
